import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';

import Loader from '../Loader/Loader';
import type { Video, VideoResponse } from '@/types/video';
import ArrowRightIcon from '../Icons/Action/ArrowRightIcon';
import { useSelectedCategories } from '@/redux/selectors/categorySelector';
import Link from 'next/link';
import { ROUTES } from '@/config/routes';
import { useSelectedLanguage } from '@/redux/selectors/languageSelector';
import { T } from '@tolgee/react';
import { getVideos } from '@/services/strapi/strapiCategoryService';
import SingleVideoCard from '../Video/SingleVideoCard';

const NextArrow = ({ onClick, isVisible }: { onClick?: () => void; isVisible: boolean }) => (
  <div
    className={`absolute right-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center transition-opacity duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
    }`}
    onClick={onClick}
  >
    <ArrowRightIcon width={20} height={20} />
  </div>
);

const PrevArrow = ({ onClick, currentSlide }: { onClick?: () => void; currentSlide: number }) =>
  currentSlide > 0 ? (
    <div
      className="absolute left-6 z-10 top-0 bottom-0 m-auto cursor-pointer rounded-[100px] border border-white bg-white/30 backdrop-blur-[10px] w-[60px] h-[60px] flex items-center justify-center"
      onClick={onClick}
    >
      <ArrowRightIcon width={20} height={20} className="transform rotate-180" />
    </div>
  ) : null;

const VideoSlider: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const selectedCategories = useSelectedCategories();
  const locale = useSelectedLanguage();

  useEffect(() => {
    const fetchVideos = async () => {
      setLoading(true);
      try {
        const categorySlugs = selectedCategories.filter((category) => category !== 'all');
        const response = await getVideos(5, categorySlugs, 0, locale);

        const mappedVideos: Video[] = response.data.map((video: VideoResponse) => ({
          imageAlt: video.attributes.title,
          imageSrc: video.attributes.thumbnail?.data?.attributes.url,
          title: video.attributes.title,
          author: video.attributes.expert?.data?.attributes.name || 'Jana Richelshagen',
          duration: '00:00',
          type: 'video',
          id: video.id,
          videoUrl: video.attributes.videoUrl,
          languages: Array.isArray(video.attributes.languages?.data)
            ? video.attributes.languages.data
                ?.map((language) => language.attributes.name)
                ?.join(', ')
            : '',
        }));

        setVideos(mappedVideos);
      } catch (error) {
        console.error('Error fetching videos:', error);
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    void fetchVideos();
  }, [selectedCategories, locale]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = videos.length;
  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3.06,
    slidesToScroll: 1,
    centerMode: false,
    nextArrow: <NextArrow isVisible={currentSlide < slideCount - 3} />,
    prevArrow: <PrevArrow currentSlide={currentSlide} />,
    beforeChange: (current: number, next: number) => setCurrentSlide(Math.round(next)),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2.2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.2,
          slidesToScroll: 1,
        },
      },
    ],
  };

  if (loading) return <Loader position={'absolute'} />;
  if (error) return <div>Error loading videos</div>;
  if (!videos.length) return null;

  return (
    <div className="relative p-5 md:p-12 pr-0 mb-3 w-full">
      <div className="relative">
        <h2 className="text-4xl">
          <T>home.filtered.videos_section.title.label</T>
        </h2>
      </div>
      <Slider {...settings} className="mt-4">
        {videos.map((video, index) => (
          <div key={index}>
            <Link href={ROUTES.VIDEO(video.id, video.title)} passHref prefetch={true}>
              <SingleVideoCard video={video} />
            </Link>
          </div>
        ))}
      </Slider>
    </div>
  );
};

export default VideoSlider;
